import { useEffect, useState } from "react";
import { Briefcase, Send } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { usePortal } from "@/context/PortalContext";
import { useI18n } from "@/context/I18nContext";
import { Card, Btn, Loader } from "@/components/portal/ui";
import { StatusBadge } from "@/components/portal/StatusBadge";

// Optional jobs module — candidate area: profile + open jobs (apply) + my applications.
export function CandidateArea() {
  const { portalKey } = usePortal();
  const { L } = useI18n();
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [prof, setProf] = useState({ headline: "", bio: "", skills: "" });
  const [jobs, setJobs] = useState([]);
  const [apps, setApps] = useState([]);

  const load = async () => {
    const [p, j, a] = await Promise.all([
      api.get("/candidates/me").catch(() => ({ data: null })),
      api.get("/jobs").catch(() => ({ data: [] })),
      api.get("/applications").catch(() => ({ data: [] })),
    ]);
    if (p.data) setProf({ headline: p.data.headline || "", bio: p.data.bio || "", skills: (p.data.skills || []).join(", ") });
    setJobs(j.data || []);
    setApps(a.data || []);
    setLoaded(true);
  };
  useEffect(() => { load().catch(() => setLoaded(true)); /* eslint-disable-next-line */ }, [portalKey]);

  const st = { background: "var(--bg)", borderColor: "var(--border)", borderRadius: "var(--radius)", color: "var(--text)" };
  const fieldCls = "w-full px-3.5 h-11 text-sm border outline-none";
  const appliedIds = new Set(apps.map((a) => a.job_id));

  const saveProfile = async () => {
    setSaving(true);
    try {
      await api.post("/candidates", { headline: prof.headline, bio: prof.bio, skills: prof.skills.split(",").map((s) => s.trim()).filter(Boolean) });
      toast.success(L({ de: "Profil gespeichert", fr: "Profil enregistré", it: "Profilo salvato", en: "Profile saved" }));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const apply = async (jobId) => {
    try { await api.post("/applications", { job_id: jobId }); toast.success(L({ de: "Beworben", fr: "Postulé", it: "Candidatura inviata", en: "Applied" })); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!loaded) return <Loader />;

  return (
    <div className="space-y-6" data-testid="candidate-area">
      <Card className="p-5 space-y-3">
        <div className="font-heading text-lg font-bold" style={{ color: "var(--text)" }}>{L({ de: "Bewerberprofil", fr: "Profil candidat", it: "Profilo candidato", en: "Candidate profile" })}</div>
        <input data-testid="cand-headline" className={fieldCls} style={st} placeholder={L({ de: "Kurzprofil / Titel", fr: "Titre", it: "Titolo", en: "Headline" })} value={prof.headline} onChange={(e) => setProf({ ...prof, headline: e.target.value })} />
        <input data-testid="cand-skills" className={fieldCls} style={st} placeholder={L({ de: "Fähigkeiten (Komma-getrennt)", fr: "Compétences (séparées par virgule)", it: "Competenze (separate da virgola)", en: "Skills (comma separated)" })} value={prof.skills} onChange={(e) => setProf({ ...prof, skills: e.target.value })} />
        <textarea data-testid="cand-bio" rows={3} className="w-full px-3.5 py-2.5 text-sm border outline-none" style={st} placeholder={L({ de: "Über mich", fr: "À propos", it: "Su di me", en: "About me" })} value={prof.bio} onChange={(e) => setProf({ ...prof, bio: e.target.value })} />
        <Btn onClick={saveProfile} disabled={saving} data-testid="cand-save">{L({ de: "Profil speichern", fr: "Enregistrer", it: "Salva", en: "Save profile" })}</Btn>
      </Card>

      <div>
        <div className="font-heading text-lg font-bold mb-3" style={{ color: "var(--text)" }}>{L({ de: "Offene Stellen", fr: "Postes ouverts", it: "Posizioni aperte", en: "Open jobs" })}</div>
        {jobs.length === 0 ? (
          <Card className="p-6 text-center text-sm" style={{ color: "var(--text-muted)" }}>{L({ de: "Aktuell keine offenen Stellen.", fr: "Aucun poste ouvert.", it: "Nessuna posizione aperta.", en: "No open jobs." })}</Card>
        ) : (
          <div className="space-y-3">
            {jobs.map((j) => (
              <Card key={j.id} className="p-4 flex items-center gap-3" data-testid={`open-job-${j.id}`}>
                <Briefcase className="h-5 w-5 shrink-0" style={{ color: "var(--accent)" }} />
                <div className="min-w-0"><div className="font-semibold truncate" style={{ color: "var(--text)" }}>{j.title}</div><div className="text-xs" style={{ color: "var(--text-muted)" }}>{j.provider_name}</div></div>
                <div className="ml-auto">
                  {appliedIds.has(j.id) ? <StatusBadge status="new" /> : <Btn variant="soft" onClick={() => apply(j.id)} data-testid={`apply-${j.id}`}><Send className="h-4 w-4" /> {L({ de: "Bewerben", fr: "Postuler", it: "Candidati", en: "Apply" })}</Btn>}
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      <div>
        <div className="font-heading text-lg font-bold mb-3" style={{ color: "var(--text)" }}>{L({ de: "Meine Bewerbungen", fr: "Mes candidatures", it: "Le mie candidature", en: "My applications" })}</div>
        {apps.length === 0 ? (
          <Card className="p-6 text-center text-sm" style={{ color: "var(--text-muted)" }}>{L({ de: "Noch keine Bewerbungen.", fr: "Aucune candidature.", it: "Nessuna candidatura.", en: "No applications yet." })}</Card>
        ) : (
          <div className="space-y-3">
            {apps.map((a) => (
              <Card key={a.id} className="p-4 flex items-center gap-3" data-testid={`my-application-${a.id}`}>
                <div className="min-w-0"><div className="font-semibold truncate" style={{ color: "var(--text)" }}>{a.job_title}</div></div>
                <div className="ml-auto"><StatusBadge status={a.status} /></div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
